// MobileDrawer.jsx — drawer pra sidebars em mobile (hidden em lg+).
//
// Wrapper pattern: o sidebar permanece "hidden lg:flex" no layout.
// Em mobile, um botão hamburger abre este drawer com mesmo conteúdo.
//
// Sprint v8.3 — responsividade iPhone 15. Reusa overlay pattern dos
// outros componentes (backdrop + slide-in left + safe-area-inset).

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

export default function MobileDrawer({ open, onClose, title, children }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Lock body scroll quando drawer aberto + fecha em Esc
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const original = document.body.style.overflow;
    dialogRef.current?.querySelector('button')?.focus();
    document.body.style.overflow = 'hidden';
    function onKey(e) {
      if (e.key === 'Escape') closeRef.current?.();
      if (e.key !== 'Tab') return;
      const items = [...(dialogRef.current?.querySelectorAll('button:not([disabled]), input, select, summary, a[href]') || [])].filter((el) => el.getClientRects().length);
      if (!items.length) return;
      if (e.shiftKey && document.activeElement === items[0]) { e.preventDefault(); items.at(-1).focus(); }
      if (!e.shiftKey && document.activeElement === items.at(-1)) { e.preventDefault(); items[0].focus(); }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = original;
      previous?.focus?.();
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        aria-label="Fechar"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Menu'}
        className="absolute inset-y-0 left-0 flex w-[min(85vw,360px)] flex-col border-r border-apolo-borderSubtle bg-apolo-surface1 shadow-2xl"
        style={{
          // Safe area pro notch + gesture bar
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
        }}
      >
        {title && (
          <header className="flex items-center justify-between border-b border-apolo-borderSubtle px-4 py-3">
            <h2 className="text-sm font-medium text-apolo-textPrimary">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  );
}
